import { Inject, Injectable, NgZone } from '@angular/core';
import { WINDOW_INJECTION_TOKEN } from '@core/constants/injection-tokens';
import {
  Observable,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  tap,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ResponsivenessService {
  private readonly LARGE_SCREEN_THRESHOLD = 768;
  private readonly XLARGE_SCREEN_THRESHOLD = 970;
  
  private readonly resize$: Observable<number> = new Observable<number>(
    (observer) => {
      let handler: () => void;

      this.ngZone.runOutsideAngular(() => {
        handler = () => {
          observer.next(this.window.innerWidth);
        };
        this.window.addEventListener('resize', handler);
      });

      return () => {
        this.window.removeEventListener('resize', handler);
      };
    }
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  constructor(
    @Inject(WINDOW_INJECTION_TOKEN) private window: Window,
    private ngZone: NgZone
  ) {}

  public getResizableListener(){
    return this.resize$.pipe(
      startWith(this.window.innerWidth)
    )
  }

  public isMoreThan$(threshold: number): Observable<boolean> {
    return this.resize$.pipe(
      startWith(this.window.innerWidth),
      map((width) => width >= threshold),
      distinctUntilChanged(),
      tap(() => {
        this.ngZone.run(() => {
          setTimeout(() => {});
        });
      })
    );
  }

  public isScreenLarge$(){
    return this.isMoreThan$(this.LARGE_SCREEN_THRESHOLD)
  }

  public isScreenLarge(){
    return this.window.innerWidth > this.LARGE_SCREEN_THRESHOLD
  }

  public isScreenLessThan(threshold: number){
    return this.window.innerWidth < threshold
  }

  public isScreenBetween(min: number, max: number){
    return this.window.innerWidth >= min && this.window.innerWidth < max
  }
  
  public isScreenXLarge$(){
    return this.isMoreThan$(this.XLARGE_SCREEN_THRESHOLD)
  }
}
